import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, MapPin, Briefcase, Calendar, Globe, BookmarkPlus, ExternalLink, Layers, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SERIF = { fontFamily: "'Bodoni Moda', Georgia, serif" };
const MONO  = { fontFamily: "'IBM Plex Mono', monospace" };

// ── DNA Bar (for intelligence tier) ─────────────────────────────────────────
const MiniDNABar = ({ label, score, icon: Icon }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3 text-muted-foreground" strokeWidth={1.5} />}
        <span style={{ ...MONO, fontSize: '8px', letterSpacing: '0.1em' }} className="uppercase text-muted-foreground">{label}</span>
      </div>
      <span style={{ ...MONO, fontSize: '11px' }} className="text-foreground font-medium">{score ?? 0}%</span>
    </div>
    <div className="h-px bg-border">
      <div className="h-px bg-foreground transition-all duration-700" style={{ width: `${score ?? 0}%` }} />
    </div>
  </div>
);

// ── Row in the comparison table ──────────────────────────────────────────────
const CompareRow = ({ label, values, highlight }) => {
  // Determine "best" value for highlighting (numeric comparison)
  const nums = values.map(v => parseFloat(v));
  const allNumeric = nums.every(n => !isNaN(n));
  const max = allNumeric ? Math.max(...nums) : null;

  return (
    <div className="grid border-b border-border last:border-0"
      style={{ gridTemplateColumns: `140px repeat(${values.length}, 1fr)` }}>
      <div className="py-3 px-4 flex items-center">
        <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="text-muted-foreground uppercase">{label}</span>
      </div>
      {values.map((v, i) => (
        <div key={i} className={`py-3 px-4 border-l border-border text-sm flex items-center ${
          highlight && allNumeric && parseFloat(v) === max && max !== 0 ? 'font-semibold text-foreground' : 'text-foreground/70'
        }`}>
          {v || '—'}
        </div>
      ))}
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const ComparePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);

  const isIntelligence = user?.subscription_plan === 'intelligence';

  useEffect(() => {
    const stored = sessionStorage.getItem('compareList');
    if (!stored) { navigate('/gallery'); return; }
    try {
      const list = JSON.parse(stored);
      if (!list?.length) { navigate('/gallery'); return; }
      // Fetch full candidate data for each
      Promise.all(list.map(c =>
        authFetch(`${BACKEND}/api/candidates/${c.id}`)
          .then(r => r.json())
          .then(d => d.success ? d.data : c)
          .catch(() => c)
      )).then(full => {
        setCandidates(full);
        setLoading(false);
      });
    } catch { navigate('/gallery'); }
  }, []); // eslint-disable-line

  const addToPipeline = async (candidateId, name) => {
    setAdding(candidateId);
    try {
      const res = await authFetch(`${BACKEND}/api/matchmaker/add-to-pipeline`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_ids: [candidateId], stage: 'saved' }),
      });
      if (res.ok) toast.success(`${name} added to pipeline`);
      else toast.error('Could not add to pipeline');
    } catch { toast.error('Could not add to pipeline'); }
    finally { setAdding(null); }
  };

  const parseCultural = (meta) => {
    if (!meta) return {};
    if (typeof meta === 'string') { try { return JSON.parse(meta); } catch { return {}; } }
    return meta;
  };

  const computeSkillDNA = (c) => {
    const all = [...(c.software_skills || []), ...(c.manual_creative_skills || [])];
    if (!all.length) return 0;
    return Math.round((all.reduce((s, x) => s + (x.level || 0), 0) / all.length / 5) * 100);
  };

  const computeCulturalDNA = (c) => {
    const cm = parseCultural(c.cultural_metadata);
    const tags = [...(cm.historian_tags || []), ...(cm.designer_archetypes || []), ...(cm.subculture_vibe || [])];
    if (!tags.length) return 0;
    return Math.min(96, 18 + tags.length * 10);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-6 h-6 border border-foreground border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const rows = [
    { label: 'Rating',       values: candidates.map(c => c.overall_ranking ? `${c.overall_ranking}/10` : '—'), highlight: true },
    { label: 'Seniority',    values: candidates.map(c => c.seniority || '—') },
    { label: 'Experience',   values: candidates.map(c => c.years_experience ? `${c.years_experience}` : '—'), highlight: true },
    { label: 'Location',     values: candidates.map(c => [c.location_city, c.location_country].filter(Boolean).join(', ') || '—') },
    { label: 'Availability', values: candidates.map(c => c.availability || '—') },
    { label: 'Work Mode',    values: candidates.map(c => (c.work_preferences || []).join(', ') || '—') },
    { label: 'Relocation',   values: candidates.map(c => c.willing_to_relocate ? 'Open' : 'No') },
    { label: 'Languages',    values: candidates.map(c => (c.languages || []).map(l => l.language).join(', ') || '—') },
    { label: 'Skills',       values: candidates.map(c => [...(c.software_skills || []), ...(c.manual_creative_skills || [])].map(s => s.name).slice(0, 3).join(', ') || '—') },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="compare-page">
      {/* Nav strip */}
      <div className="border-b border-border px-6 lg:px-12 py-4 sticky top-0 bg-white z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="compare-back-btn">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span style={{ ...MONO, fontSize: '10px', letterSpacing: '0.12em' }} className="uppercase">Back to Gallery</span>
          </button>
          <div className="flex items-center gap-3">
            <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }} className="text-muted-foreground uppercase hidden sm:block">
              Comparing {candidates.length} profiles
            </span>
            {!isIntelligence && (
              <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
                className="border border-border text-muted-foreground px-2 py-0.5 uppercase text-xs">
                AI DNA on Intelligence
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {/* Header */}
        <div className="mb-10">
          <h1 style={{ ...SERIF, fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.05 }} className="font-semibold text-foreground mb-2">
            Compare & Decide
          </h1>
          <p style={{ ...MONO, fontSize: '10px', letterSpacing: '0.12em' }} className="text-muted-foreground uppercase">
            Side-by-side comparison · {candidates.length} candidates
          </p>
        </div>

        {/* Candidate photo grid */}
        <div className="grid border border-border mb-0" style={{ gridTemplateColumns: `140px repeat(${candidates.length}, 1fr)` }}>
          <div className="bg-[hsl(0,0%,98%)] p-4 border-r border-border" />
          {candidates.map((c) => (
            <div key={c.id} className="border-l border-border first:border-l-0">
              {/* Photo */}
              <div className="aspect-[3/4] overflow-hidden bg-secondary">
                <img src={c.photo_url} alt={c.full_name} className="w-full h-full object-cover"
                  style={{ filter: 'grayscale(0.15)' }} />
              </div>
              {/* Identity */}
              <div className="p-4 border-t border-border">
                <button onClick={() => navigate(`/candidate/${c.id}`)}
                  className="text-left w-full group" data-testid={`compare-candidate-${c.id}`}>
                  <h3 style={SERIF} className="font-semibold text-sm group-hover:underline truncate">{c.full_name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{c.seniority} {c.core_role}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3 text-muted-foreground/50" strokeWidth={1.5} />
                    <span className="text-xs text-muted-foreground/60 truncate">{c.location_city}</span>
                  </div>
                  {c.overall_ranking && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <Star className="w-3 h-3 fill-foreground text-foreground" strokeWidth={0} />
                      <span style={MONO} className="text-xs font-medium">{c.overall_ranking}/10</span>
                    </div>
                  )}
                </button>
                <Button
                  onClick={() => addToPipeline(c.id, c.full_name)}
                  disabled={adding === c.id}
                  variant="outline"
                  className="rounded-none w-full mt-3 text-xs h-7 gap-1"
                  data-testid={`add-to-pipeline-${c.id}`}>
                  <BookmarkPlus className="w-3 h-3" strokeWidth={1.5} />
                  {adding === c.id ? 'Adding...' : 'Add to Pipeline'}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <div className="border border-t-0 border-border">
          {/* Section: Core Details */}
          <div className="bg-[hsl(0,0%,98%)] border-b border-border px-4 py-2.5">
            <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }} className="text-muted-foreground uppercase">Core Details</span>
          </div>
          {rows.map(row => <CompareRow key={row.label} {...row} />)}

          {/* Section: AI DNA — Intelligence only */}
          <div className="bg-[hsl(0,0%,98%)] border-b border-border border-t border-border px-4 py-2.5 flex items-center justify-between">
            <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }} className="text-muted-foreground uppercase">AI DNA Intelligence</span>
            {!isIntelligence && (
              <button onClick={() => navigate('/pricing')}
                style={{ ...MONO, fontSize: '8px', letterSpacing: '0.1em' }}
                className="text-muted-foreground/50 border border-border px-2 py-0.5 uppercase hover:border-foreground hover:text-foreground transition-colors">
                Upgrade
              </button>
            )}
          </div>

          {isIntelligence ? (
            <div className="grid border-b border-border last:border-0"
              style={{ gridTemplateColumns: `140px repeat(${candidates.length}, 1fr)` }}>
              <div className="py-4 px-4 bg-[hsl(0,0%,98%)] border-r border-border" />
              {candidates.map(c => {
                const skillDNA    = computeSkillDNA(c);
                const culturalDNA = computeCulturalDNA(c);
                const trendDNA    = Math.round((c.trend_alignment || 0) * 100);
                return (
                  <div key={c.id} className="py-4 px-4 border-l border-border space-y-4">
                    <MiniDNABar label="Skill" score={skillDNA} icon={Layers} />
                    <MiniDNABar label="Cultural" score={culturalDNA} icon={Users} />
                    <MiniDNABar label="Trend" score={trendDNA} icon={TrendingUp} />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid border-b border-border" style={{ gridTemplateColumns: `140px repeat(${candidates.length}, 1fr)` }}>
              <div className="py-6 px-4 bg-[hsl(0,0%,98%)] border-r border-border" />
              {candidates.map(c => (
                <div key={c.id} className="py-6 px-4 border-l border-border">
                  <div className="space-y-2 opacity-25 pointer-events-none">
                    {['Skill', 'Cultural', 'Trend'].map(l => (
                      <div key={l}>
                        <div className="flex justify-between mb-1">
                          <span style={{ ...MONO, fontSize: '8px' }} className="text-muted-foreground uppercase">{l}</span>
                          <span style={{ ...MONO, fontSize: '9px' }}>—%</span>
                        </div>
                        <div className="h-px bg-border" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Section: Style DNA */}
          <div className="bg-[hsl(0,0%,98%)] border-b border-border border-t border-border px-4 py-2.5">
            <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }} className="text-muted-foreground uppercase">Style DNA</span>
          </div>
          <div className="grid border-b border-border last:border-0"
            style={{ gridTemplateColumns: `140px repeat(${candidates.length}, 1fr)` }}>
            <div className="py-4 px-4 bg-[hsl(0,0%,98%)] border-r border-border" />
            {candidates.map(c => {
              const cm = parseCultural(c.cultural_metadata);
              const tags = [...(cm.designer_archetypes || []).slice(0, 2), ...(cm.historian_tags || []).slice(0, 1)];
              return (
                <div key={c.id} className="py-4 px-4 border-l border-border">
                  {tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {tags.map((t, i) => (
                        <span key={i} style={{ ...MONO, fontSize: '8px', letterSpacing: '0.06em' }}
                          className="border border-border px-1.5 py-0.5 text-muted-foreground uppercase">
                          {t.replace(/-/g, ' · ')}
                        </span>
                      ))}
                    </div>
                  ) : <span className="text-sm text-muted-foreground">—</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer actions */}
        <div className="mt-6 flex items-center justify-between gap-4 py-4 border-t border-border">
          <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }} className="text-muted-foreground uppercase">
            {candidates.length} profiles compared
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/gallery')} className="rounded-none text-sm">
              Back to Gallery
            </Button>
            <Button
              onClick={async () => {
                for (const c of candidates) await addToPipeline(c.id, c.full_name);
              }}
              className="rounded-none bg-foreground text-background hover:bg-foreground/80 text-sm gap-2"
              data-testid="add-all-pipeline-btn">
              <BookmarkPlus className="w-4 h-4" strokeWidth={1.5} />
              Add All to Pipeline
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparePage;
